// EDEN · materials client — the Teacher Pack + exercise sheet for a curriculum node, plus (WEB-P2)
// the node's ranked Resources list: the same pack/sheet, folded together with the curated book
// page pins (`_shelf/week_pins.json`) and the WK:→LES:/PRAC: citation graph
// (`curriculum/materials-links.json`), ranked and capped by `scripts/sync-materials.mjs`.
//
// `curriculum/materials-index.json` is GENERATED by `scripts/sync-materials.mjs` (prebuild) from
// the Teacher Pack fleet's sidecars; the files it points at are copied into `public/materials/`
// and served verbatim by Pages. The node id is the join key — this module never derives a path
// from a grade/term/week, it only looks the node up. A node with no entry simply has no pack
// yet (the exercise layer and the fleet are still being filled); screens say so calmly.
//
// Resources are citations or served original materials: pack/sheet links and hash-validated
// practice companions from curriculum/exercise-companions.json may carry an href. Publisher
// source files remain citations, not hosted assets. `tier`/`permission` are set
// only where a licence tier is actually known (a practice-layer node's own field, or a book id the
// Licence Register names by pattern) — see the generator's own comment for what is deliberately
// left unmarked rather than guessed.

import { loadItemBank, type ItemBankCard } from "./itembank";

export interface NodeMaterials {
  grade: number;
  term: number;
  week: number;
  /** Absolute site path to the Teacher Pack PDF, or null when this node has no pack at all.
   *  Null since T1-LANE-C (2026-09-17): owner ruling 4 retired the PDF pack fleet for G8-G10, so
   *  those grades reach the app as SHEET-ONLY nodes, found by the authored exercise layer rather
   *  than by a pack sidecar. Every render site must branch on it — a null pack is a link that is
   *  not drawn, never a dead anchor and never a hidden row. */
  pack: string | null;
  /** Absolute site path to the rendered exercise sheet, or null when that week has none yet. */
  sheet: string | null;
  title: string;
  oneliner: string;
  coursebook_note: string;
  /** The pack sidecar's own rhythm.thread/fluency.note (WEB-P3) — the same "Classroom activity"
   *  pointer coursebook_note always had, now available for the other two strands too. Empty string
   *  when the sidecar has no note for that strand (never absent — see sync-materials.mjs). */
  thread_note: string;
  fluency_note: string;
}

export interface Resource {
  kind: "pack" | "sheet" | "reference" | "practice" | "audio";
  /** Stable across a re-sync as long as the underlying pin/edge/pack-sheet is unchanged — this is
   *  the id the per-teacher "use this" tick (`/api/materials/use`) stores. */
  key: string;
  title: string;
  detail?: string;
  /** Served pack/sheet or hash-validated original practice companion. */
  href?: string;
  /** Compulsory worksheet companion: keep its link visible beyond resource folds. */
  required?: boolean;
  tier?: "A" | "B" | "C" | null;
  permission?: string;
}

/** WEB-P3: the node's edges split by strand — coursebook/fluency as one ordered array (every
 *  item equally "the coursebook"/"the fluency block"), thread as three tiers (its three edge
 *  types genuinely differ in join strength — see sync-materials.mjs's own comment). Absent on a
 *  node with no `strands` field at all (a stale pre-WEB-P3 committed index) — callers treat that
 *  the same as "no strand-specific resources, fall back to the merged list". */
export interface NodeStrandResources {
  coursebook: Resource[];
  thread: { core: Resource[]; practice: Resource[]; extension: Resource[] };
  fluency: Resource[];
}

export interface NodeResources {
  /** The FULL ranked list (WEB-P2R Fix 1) — nothing is dropped at build time any more. `pack`/
   *  `sheet` entries are always all shown (materialsCard's own two links); `more` counts how many
   *  of the REST (everything else in `items`) sit beyond the Week card's default fold, so a caller
   *  can recover the split with no extra constant to keep in step — see this-week.ts's
   *  `resourcesCard`, which filters `pack`/`sheet` out first and then slices at `rows.length - more`. */
  items: Resource[];
  more: number;
  strands?: NodeStrandResources;
}

interface MaterialsIndex {
  schema: number;
  counts: { packs: number; sheets: number; grades: Record<string, { packs: number; sheets: number }> };
  nodes: Record<string, NodeMaterials>;
  /** Absent on a stale (schema-1) committed index — callers treat that the same as "no resources". */
  resources?: Record<string, NodeResources>;
}

let cache: Promise<MaterialsIndex | null> | null = null;

/** The whole index, loaded once (code-split by Vite, cached by the shell). ~1.9 MB raw / ~93 KB
 *  gzip as of WEB-P2R (2026-09-05) now that Fix 1 ships every resource, not just the capped few —
 *  re-measure with `gzip -c curriculum/materials-index.json | wc -c` rather than trusting this
 *  number once the fleet or the citation graph grows (CLAUDE.md rule 3: generate, don't transcribe). */
export function loadMaterialsIndex(): Promise<MaterialsIndex | null> {
  if (!cache) {
    cache = import("../curriculum/materials-index.json")
      .then((m) => (m.default ?? m) as unknown as MaterialsIndex)
      .catch(() => null);
  }
  return cache;
}

/** The pack + sheet for one node, or null when the fleet has not reached it. */
export async function materialsFor(nodeId: string | null | undefined): Promise<NodeMaterials | null> {
  if (!nodeId) return null;
  const idx = await loadMaterialsIndex();
  return idx?.nodes[nodeId] ?? null;
}

/** Every node id of one grade that the index knows — for drawing the roadmap's pack and sheet
 *  marks. NOT "every node that has a pack": since T1-LANE-C a node can be in the index with a
 *  sheet and a null pack (G8-G10, owner ruling 4), so a caller counting packs must filter on
 *  `m.pack` rather than take this map's size — see roadmap.ts's `packCount`. */
export async function materialsForGrade(grade: number): Promise<Map<string, NodeMaterials>> {
  const idx = await loadMaterialsIndex();
  const out = new Map<string, NodeMaterials>();
  if (!idx) return out;
  for (const [id, m] of Object.entries(idx.nodes)) if (m.grade === grade) out.set(id, m);
  return out;
}

/** The node's ranked Resources list (pack/sheet + pins + citation-graph edges), or null when the
 *  node has none at all — a calmer signal than an empty array for "nothing to show here yet". */
export async function resourcesFor(nodeId: string | null | undefined): Promise<NodeResources | null> {
  if (!nodeId) return null;
  const idx = await loadMaterialsIndex();
  return idx?.resources?.[nodeId] ?? null;
}

// ── WEB-P3: the lesson is a period slot ─────────────────────────────────────────────────────
//
// A slot is node / strand / ordinal (never dated — the calendar maps node_id to a day the same
// way it always has). `periods_per_week` is a CLASS field (migration 0024, owner ruling
// 2026-09-05): fluency and thread are always 2 periods each; coursebook is whatever remains. This
// stays a pure function of the count, never of any one grade file's own `rhythm` constant (that
// describes how the curriculum template was AUTHORED, not any one class's real timetable).
export interface PeriodSplit {
  coursebook: number;
  thread: number;
  fluency: number;
}

export function splitPeriods(periodsPerWeek: number): PeriodSplit {
  const total = Number.isFinite(periodsPerWeek) && periodsPerWeek > 0 ? Math.floor(periodsPerWeek) : 12;
  const fluency = Math.min(2, total);
  const thread = Math.min(2, total - fluency);
  const coursebook = Math.max(0, total - fluency - thread);
  return { coursebook, thread, fluency };
}

export const STRANDS = ["coursebook", "thread", "fluency"] as const;
export type Strand = (typeof STRANDS)[number];

/** Distribute `items` across `slotCount` ordinal buckets, in order, sizes differing by at most
 *  one — the "coursebook ordinals spread evenly across the week's teaches edges in their existing
 *  order" rule. Fewer items than slots leaves some ordinals with an empty bucket (there is no
 *  dedicated resource for every period when a node cites only 1–2 lessons across an 8-period
 *  week) — callers fall back to the strand's full list for those (see `slotView` below), never to
 *  a blank card. */
export function spreadOrdinals<T>(items: readonly T[], slotCount: number): T[][] {
  const buckets: T[][] = Array.from({ length: Math.max(0, slotCount) }, () => []);
  if (!items.length || slotCount <= 0) return buckets;
  items.forEach((item, i) => {
    const bucket = Math.min(slotCount - 1, Math.floor((i * slotCount) / items.length));
    buckets[bucket]!.push(item);
  });
  return buckets;
}

export interface SlotView {
  /** This ordinal's own pick(s) — may be empty (see `spreadOrdinals`). */
  core: Resource[];
  /** Everything else in the strand — other ordinals' picks + the wider tiers — always reachable,
   *  never hidden, so "full list always reachable" holds even on an ordinal with no dedicated item. */
  more: Resource[];
}

/** One ordinal's slice of a strand. `coreTier` is spread across ordinals; `restTiers` (thread's
 *  wider practice/extension tiers, or nothing for coursebook/fluency) are never ordinal-specific —
 *  they always sit in `more`, alongside whichever `coreTier` items this ordinal didn't get. */
export function slotView(coreTier: readonly Resource[], restTiers: readonly Resource[], ordinal: number, ordinalCount: number): SlotView {
  const buckets = spreadOrdinals(coreTier, ordinalCount);
  const idx = ordinalCount > 0 ? Math.min(Math.max(ordinal - 1, 0), ordinalCount - 1) : 0;
  const core = buckets[idx] ?? [];
  const coreKeys = new Set(core.map((r) => r.key));
  const more = [...coreTier.filter((r) => !coreKeys.has(r.key)), ...restTiers];
  return { core, more };
}

/** An item-bank card reshaped as a plain citation `Resource` — reuses the existing "practice" row
 *  rendering (this-week.ts already knows how to draw `kind: "practice"`) rather than a parallel
 *  card component. An item-bank citation is not a served file: the picker/editor is
 *  where a teacher actually opens the card. */
function cardResource(c: ItemBankCard): Resource {
  return {
    kind: "practice",
    key: `itembank:${c.id}`,
    title: c.title,
    detail: `${c.band ?? "unsupported"} · ${c.component} · ${c.difficulty}`,
    tier: null,
  };
}

/** The node's strand-split resources, folding in item-bank cards matching the node's OWN
 *  `thread.vocab_set`/`task_family` (WEB-P3: "thread ordinals → item-bank cards + PRAC: files
 *  matching the node's thread.vocab_set / task_family"). This join happens here, at read time —
 *  not in sync-materials.mjs — because the match key is exact-string (a card's `sets` literally
 *  contains the curriculum's own vocab_set id, e.g. "MOV-15-toys" on both sides) and the item-bank
 *  index is a separate, independently-synced build (`scripts/sync-itembank.mjs`) this module did
 *  not otherwise depend on; folding it in here avoids coupling the two build scripts' run order.
 *  A card matching BOTH vocab_set and task_family is the tightest join (thread.core, alongside the
 *  PRAC: edges); a card matching only one of the two is a looser match (thread.practice). Returns
 *  null when the node has no strand data at all (see `NodeStrandResources`'s own doc comment). */
export async function strandResourcesFor(
  nodeId: string | null | undefined,
  thread: { vocab_set: string | null; task_family: string | null } | null | undefined,
): Promise<NodeStrandResources | null> {
  if (!nodeId) return null;
  const idx = await loadMaterialsIndex();
  const base = idx?.resources?.[nodeId]?.strands;
  if (!base) return null;

  const vocabSet = thread?.vocab_set ?? null;
  const taskFamily = thread?.task_family ?? null;
  let coreCards: Resource[] = [];
  let practiceCards: Resource[] = [];
  if (vocabSet || taskFamily) {
    const bank = await loadItemBank();
    const gradeMatch = /^G(10|[1-9])-/.exec(nodeId);
    const grade = gradeMatch ? Number(gradeMatch[1]) : null;
    // Unknown/excluded/incomplete cards remain present in the generated audit index, but are never
    // offered as a selectable Week resource. Grade scope is checked here as well as at insertion.
    const cards = (bank?.cards ?? []).filter((card) =>
      card.selectable && grade !== null && card.grade_scope.includes(grade));
    const vocabMatch = (c: ItemBankCard) => vocabSet !== null && c.sets.includes(vocabSet);
    const taskMatch = (c: ItemBankCard) => taskFamily !== null && c.task_family === taskFamily;
    coreCards = cards.filter((c) => vocabMatch(c) && taskMatch(c)).map(cardResource);
    practiceCards = cards.filter((c) => vocabMatch(c) !== taskMatch(c)).map(cardResource);
  }

  return {
    coursebook: base.coursebook,
    fluency: base.fluency,
    thread: {
      core: [...base.thread.core, ...coreCards],
      practice: [...base.thread.practice, ...practiceCards],
      extension: base.thread.extension,
    },
  };
}
